// src/pages/ELearning/BenefitsList.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBenefits, deleteBenefit, claimBenefit } from '../../api/client';
import { Gift, Award, CheckCircle, Loader2, AlertCircle, Plus, Edit, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';

interface Benefit {
  _id: string;
  name: string;
  description: string;
  image?: string;
  requiredPoints: number;
  requiredRating: number;
  expiresAt?: string;
  createdBy?: {
    _id: string;
    name: string;
    email: string;
  };
}

const BenefitsList: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const [benefits, setBenefits] = useState<Benefit[]>([]);
  const [claimed, setClaimed] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [modalData, setModalData] = useState<{ creator: any } | null>(null);

  const fetchBenefits = async () => {
    try {
      const data = await getBenefits();
      setBenefits(data);
      if (user?.elearning?.benefits) {
        setClaimed(user.elearning.benefits.map((b: any) => b.benefit));
      }
    } catch (err: any) {
      console.error('Ошибка загрузки бенефитов:', err);
      setError(err.response?.data?.error || 'Не удалось загрузить бенефиты');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBenefits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleClaim = async (benefitId: string) => {
    setClaiming(benefitId);
    try {
      const res = await claimBenefit(benefitId);
      setClaimed([...claimed, benefitId]);
      await refreshUser();
      setModalData({ creator: res.creator });
      setShowSuccessModal(true);
      showToast('Бенефит успешно получен!', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Не удалось получить бенефит', 'error');
    } finally {
      setClaiming(null);
    }
  };

  const handleEdit = (id: string) => {
    navigate(`/app/elearning/benefits/${id}/edit`);
  };

  const handleDelete = (id: string) => {
    confirm.showConfirm({
      title: 'Удаление бенефита',
      message: 'Вы уверены, что хотите удалить этот бенефит?',
      confirmText: 'Удалить',
      onConfirm: async () => {
        try {
          await deleteBenefit(id);
          showToast('Бенефит удалён', 'success');
          fetchBenefits();
        } catch (err: any) {
          showToast(err.response?.data?.error || 'Не удалось удалить бенефит', 'error');
        }
      },
    });
  };

  const canClaim = (benefit: Benefit) => {
    if (claimed.includes(benefit._id)) return false;
    if (benefit.expiresAt && new Date(benefit.expiresAt) < new Date()) return false;
    return (user?.elearning?.rating || 0) >= benefit.requiredRating &&
           (user?.elearning?.totalPoints || 0) >= benefit.requiredPoints;
  };

  const isOwner = (benefit: any) => {
    if (!user) return false;
    // ★ Создатель ИЛИ admin компании
    if (['admin', 'superadmin'].includes(user.role)) return true;
    return String(user.id) === String(benefit.createdBy?._id || benefit.createdBy);
  };

  if (loading) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <AlertCircle size={48} color="#ff4d4f" />
          <h2>{error}</h2>
          <button onClick={fetchBenefits} style={styles.retryButton}>Повторить</button>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .bl-hero { animation: fadeUp 0.5s ease-out; }
        .bl-card { transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1); animation: fadeUp 0.5s ease-out backwards; }
        .bl-card:hover { transform: translateY(-6px); box-shadow: 0 20px 50px rgba(31, 38, 135, 0.15) !important; }
        .bl-card:hover .bl-card-img { transform: scale(1.08); }
        .bl-card-img { transition: transform 0.5s ease; }
        .bl-btn { transition: all 0.3s ease; }
        .bl-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(102, 126, 234, 0.4) !important; }
        .bl-icon-btn { transition: all 0.2s; border-radius: 10px; }
        .bl-icon-btn:hover { background: rgba(0,0,0,0.06); }
        .bl-create-btn { transition: all 0.3s ease; }
        .bl-create-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(102, 126, 234, 0.4) !important; }
      `}</style>

      {/* ═══ Hero ═══ */}
      <div className="bl-hero" style={styles.hero}>
        <div style={styles.heroIcon}>
          <Gift size={28} color="#fff" />
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={styles.title}>Бенефиты</h1>
          <p style={styles.subtitle}>Обменивайте заработанные баллы на призы и привилегии</p>
        </div>
        <button onClick={() => navigate('/app/elearning/benefits/new')} className="bl-create-btn" style={styles.createButton}>
          <Plus size={18} /> Создать бенефит
        </button>
      </div>

      {/* ═══ Grid ═══ */}
      {benefits.length === 0 ? (
        <div style={styles.empty}>
          <Gift size={56} color="#667eea" style={{ opacity: 0.4 }} />
          <h3 style={{ color: '#5a5f7a', marginTop: 16, fontWeight: 700 }}>Пока нет бенефитов</h3>
          <p style={{ color: '#8b8fa8', fontSize: 14 }}>Создайте первый бенефит для сотрудников</p>
        </div>
      ) : (
        <div style={styles.grid}>
          {benefits.map((benefit, idx) => {
            const available = canClaim(benefit);
            const isClaimed = claimed.includes(benefit._id);
            return (
              <div
                key={benefit._id}
                className="bl-card"
                style={{ ...styles.card, animationDelay: `${idx * 0.05}s` }}
              >
                {/* Image */}
                <div style={styles.imageWrap}>
                  <img
                    src={benefit.image || '/images/default-benefit.jpg'}
                    alt={benefit.name}
                    className="bl-card-img"
                    style={styles.image}
                  />
                  {/* Points badge */}
                  <div style={styles.pointsBadge}>
                    <Gift size={14} /> {benefit.requiredPoints}
                  </div>
                  {isClaimed && (
                    <div style={styles.claimedOverlay}>
                      <CheckCircle size={48} color="#fff" />
                    </div>
                  )}
                </div>

                <div style={styles.content}>
                  <h3 style={styles.name}>{benefit.name}</h3>
                  {benefit.description && (
                    <p style={styles.description}>{benefit.description}</p>
                  )}

                  <div style={styles.requirements}>
                    <div style={styles.reqItem}>
                      <Award size={14} color="#667eea" />
                      <span>Рейтинг: <strong>{benefit.requiredRating}</strong></span>
                    </div>
                  </div>

                  {benefit.expiresAt && (
                    <p style={styles.expiry}>
                      ⏰ До {new Date(benefit.expiresAt).toLocaleDateString('ru-RU')}
                    </p>
                  )}
                  {benefit.createdBy && (
                    <p style={styles.creator}>👤 {benefit.createdBy.name}</p>
                  )}

                  <div style={styles.cardActions}>
                    {isClaimed ? (
                      <button disabled style={styles.claimedButton}>
                        <CheckCircle size={16} /> Получено
                      </button>
                    ) : (
                      <button
                        onClick={() => handleClaim(benefit._id)}
                        disabled={!available || claiming === benefit._id}
                        className={available ? 'bl-btn' : ''}
                        style={available ? styles.claimButton : styles.disabledButton}
                      >
                        {claiming === benefit._id
                          ? '...'
                          : available
                          ? '🎁 Получить'
                          : '🔒 Недостаточно баллов'}
                      </button>
                    )}
                    {isOwner(benefit) && (
                      <div style={styles.adminActions}>
                        <button onClick={() => handleEdit(benefit._id)} className="bl-icon-btn" style={styles.iconButton} title="Редактировать">
                          <Edit size={16} color="#667eea" />
                        </button>
                        <button onClick={() => handleDelete(benefit._id)} className="bl-icon-btn" style={styles.iconButton} title="Удалить">
                          <Trash2 size={16} color="#ef4444" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ Success modal ═══ */}
      {showSuccessModal && modalData && (
        <div style={modalStyles.overlay} onClick={() => setShowSuccessModal(false)}>
          <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
            <div style={modalStyles.successIcon}>
              <CheckCircle size={48} color="#fff" />
            </div>
            <h3 style={modalStyles.modalTitle}>Бенефит получен!</h3>
            <p style={modalStyles.modalText}>
              Обратитесь к создателю бенефита для его получения:
            </p>
            <div style={modalStyles.creatorInfo}>
              <div style={modalStyles.creatorAvatar}>
                {(modalData.creator.name || '?').slice(0, 1).toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 700, color: '#1a1a2e' }}>{modalData.creator.name}</div>
                <div style={{ fontSize: 13, color: '#8b8fa8' }}>{modalData.creator.email}</div>
              </div>
            </div>
            <button onClick={() => setShowSuccessModal(false)} style={modalStyles.closeButton}>
              Понятно
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  hero: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28, flexWrap: 'wrap' },
  heroIcon: { width: 56, height: 56, borderRadius: 18, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 32px rgba(245, 87, 108, 0.4)', flexShrink: 0 },
  title: { fontSize: 28, fontWeight: 800, margin: 0, background: 'linear-gradient(135deg, #1a1a2e 0%, #f5576c 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '-0.5px' },
  subtitle: { color: '#8b8fa8', margin: '4px 0 0', fontSize: 14, fontWeight: 500 },
  createButton: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: 14, padding: '12px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 14, boxShadow: '0 8px 24px rgba(102, 126, 234, 0.35)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 },
  empty: { textAlign: 'center', padding: 80, background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(20px)', borderRadius: 24, border: '1px dashed rgba(102,126,234,0.3)' },
  card: { background: 'rgba(255, 255, 255, 0.55)', backdropFilter: 'blur(24px) saturate(180%)', WebkitBackdropFilter: 'blur(24px) saturate(180%)', borderRadius: 24, overflow: 'hidden', border: '1px solid rgba(255, 255, 255, 0.7)', boxShadow: '0 8px 32px rgba(31, 38, 135, 0.08)', display: 'flex', flexDirection: 'column' },
  imageWrap: { position: 'relative', overflow: 'hidden', height: 180 },
  image: { width: '100%', height: '100%', objectFit: 'cover' },
  pointsBadge: { position: 'absolute', top: 12, right: 12, background: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)', color: '#fff', padding: '6px 14px', borderRadius: 12, fontSize: 13, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 4px 12px rgba(246, 211, 101, 0.5)' },
  claimedOverlay: { position: 'absolute', inset: 0, background: 'rgba(16, 185, 129, 0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' },
  content: { padding: 20, display: 'flex', flexDirection: 'column', flex: 1 },
  name: { fontSize: 18, fontWeight: 700, margin: '0 0 8px', color: '#1a1a2e' },
  description: { fontSize: 13, color: '#5a5f7a', margin: '0 0 14px', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  requirements: { display: 'flex', gap: 12, marginBottom: 10 },
  reqItem: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#5a5f7a', padding: '4px 10px', background: 'rgba(102, 126, 234, 0.08)', borderRadius: 10, fontWeight: 600 },
  expiry: { fontSize: 11, color: '#f59e0b', margin: '0 0 4px', fontWeight: 600 },
  creator: { fontSize: 11, color: '#8b8fa8', margin: '0 0 14px' },
  cardActions: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', gap: 8 },
  claimButton: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: 12, padding: '11px 18px', cursor: 'pointer', fontWeight: 700, flex: 1, fontSize: 14, boxShadow: '0 6px 20px rgba(102, 126, 234, 0.3)' },
  disabledButton: { background: 'rgba(0,0,0,0.06)', color: '#8b8fa8', border: 'none', borderRadius: 12, padding: '11px 18px', cursor: 'not-allowed', fontWeight: 600, flex: 1, fontSize: 13 },
  claimedButton: { background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white', border: 'none', borderRadius: 12, padding: '11px 18px', cursor: 'default', fontWeight: 700, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 14 },
  adminActions: { display: 'flex', gap: 4 },
  iconButton: { background: 'transparent', border: 'none', cursor: 'pointer', padding: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  retryButton: { marginTop: 20, padding: '10px 24px', background: '#2575fc', color: 'white', border: 'none', borderRadius: 30, cursor: 'pointer', fontWeight: 600 }
};

const modalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.4)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    animation: 'fadeUp 0.3s ease-out',
  },
  modal: {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(24px) saturate(180%)',
    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
    borderRadius: 28,
    padding: 32,
    width: 420,
    maxWidth: '90%',
    textAlign: 'center',
    boxShadow: '0 24px 64px rgba(31, 38, 135, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.7)',
  },
  successIcon: {
    width: 80,
    height: 80,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    boxShadow: '0 12px 32px rgba(17, 153, 142, 0.4)',
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 800,
    marginBottom: 12,
    color: '#1a1a2e',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #11998e 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  modalText: {
    fontSize: 14,
    color: '#5a5f7a',
    marginBottom: 20,
    lineHeight: 1.5,
  },
  creatorInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    background: 'rgba(102, 126, 234, 0.08)',
    padding: '16px',
    borderRadius: 16,
    marginBottom: 24,
    textAlign: 'left',
  },
  creatorAvatar: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: 20,
    flexShrink: 0,
  },
  closeButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: 14,
    padding: '14px 24px',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 15,
    width: '100%',
    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.35)',
  },
};

export default BenefitsList;